import { DesiredSettingValue } from '@crossnokaye/typescript-sdk/models/components';

import type { PreviewSaveStatus } from './setting-preview.types';

export interface UpdateSettingsMutationEntry {
  status: 'pending' | 'success' | 'error' | 'idle';
  submittedAt: number;
  variables: DesiredSettingValue[] | undefined;
}

interface DeriveSaveProgressByChangeIdParams {
  targetSettingIds: Set<string>;
  queuedBySettingId: Set<string>;
  cancelledBySettingId: Set<string>;
  mutationEntries: UpdateSettingsMutationEntry[];
  saveRunStartedAt: number | null;
  settingIdToChangeIds: Map<string, string[]>;
}

export const deriveSaveProgressByChangeId = ({
  targetSettingIds,
  queuedBySettingId,
  cancelledBySettingId,
  mutationEntries,
  saveRunStartedAt,
  settingIdToChangeIds,
}: DeriveSaveProgressByChangeIdParams): Record<string, PreviewSaveStatus> => {
  const settingStatusMap = new Map<string, PreviewSaveStatus>();

  for (const settingId of targetSettingIds) {
    if (queuedBySettingId.has(settingId)) {
      settingStatusMap.set(settingId, 'queued');
      continue;
    }
    if (cancelledBySettingId.has(settingId)) {
      settingStatusMap.set(settingId, 'cancelled');
      continue;
    }

    let latestStatus: 'pending' | 'success' | 'error' | 'idle' | null = null;
    let latestSubmittedAt = -1;
    for (const entry of mutationEntries) {
      if (!entry.variables?.some((s) => s.settingId === settingId)) continue;
      if (saveRunStartedAt && entry.submittedAt < saveRunStartedAt) continue;
      if (entry.submittedAt >= latestSubmittedAt) {
        latestSubmittedAt = entry.submittedAt;
        latestStatus = entry.status;
      }
    }

    if (latestStatus === 'pending') settingStatusMap.set(settingId, 'pending');
    else if (latestStatus === 'success') settingStatusMap.set(settingId, 'completed');
    else if (latestStatus === 'error') settingStatusMap.set(settingId, 'failed');
    else settingStatusMap.set(settingId, 'idle');
  }

  const progressByChangeId: Record<string, PreviewSaveStatus> = {};
  for (const [settingId, changeIds] of settingIdToChangeIds.entries()) {
    const status = settingStatusMap.get(settingId) ?? 'idle';
    for (const changeId of changeIds) {
      progressByChangeId[changeId] = status;
    }
  }

  return progressByChangeId;
};
